import React, { useEffect, useState } from "react";
import api, { brl, fmtDate, hojeISO, rotuloParcela } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ComunicarClienteDialog } from "@/components/ComunicarClienteDialog";
import { brlOrDash } from "@/lib/refresh";

const FORMAS = ["PIX", "Boleto", "Transferência", "Dinheiro", "Cartão"];
const TIPOS = [["integral", "Valor integral"], ["parcial", "Valor parcial"], ["juros", "Somente juros/encargos"]];
const nowHM = () => new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", hour12: false }).slice(0, 5);
const n = (v) => Number(v) || 0;
const FF = ({ l, children, t }) => (<div data-testid={t}><Label className="text-xs">{l}</Label><div className="mt-1">{children}</div></div>);

const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

const Resumo = ({ enc, f, restante }) => {
  const encargosDev = n(f.juros) + n(f.multa);
  const atualizado = enc.saldo_principal + encargosDev - (f.tipo === "juros" ? 0 : n(f.desconto));
  const recebido = n(f.valor_pago);
  const encPagos = Math.min(recebido, encargosDev);
  const saldoTotal = f.tipo === "juros" ? enc.saldo_principal + Math.max(0, encargosDev - recebido) : Math.max(0, restante + Math.max(0, encargosDev - encPagos));
  const Item = ({ l, v, t, cls = "" }) => (<div className="min-w-0"><div className="text-[11px] uppercase tracking-wide text-slate-500">{l}</div><div className={`mono font-bold num-nowrap ${cls}`} data-testid={t}>{brlOrDash(v)}</div></div>);
  return (
    <div className="sticky top-0 z-10 rounded-lg border bg-white shadow-sm p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm" data-testid="baixa-resumo-fixo">
      <Item l="Valor original" v={enc.valor_original ?? enc.saldo_principal + n(enc.valor_ja_pago)} t="resumo-original" />
      <Item l="Valor atualizado" v={Math.max(0, atualizado)} t="resumo-atualizado" cls={encargosDev > 0 ? "text-amber-700" : ""} />
      <Item l="Valor recebido" v={recebido} t="resumo-recebido" cls="text-emerald-700" />
      <Item l="Saldo restante" v={saldoTotal} t="resumo-saldo" cls={saldoTotal > 0.005 ? "text-red-700" : "text-emerald-700"} />
    </div>
  );
};

export function BaixaModal({ inst, onClose, onDone }) {
  const [f, setF] = useState({});
  const [enc, setEnc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pago, setPago] = useState(null);
  const [erro, setErro] = useState("");
  const [idem, setIdem] = useState("");
  const [verificando, setVerificando] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Reinicia o formulário só quando muda a PARCELA (id), não quando o objeto é recriado por uma atualização da lista
  const instId = inst?.id;
  useEffect(() => {
    if (instId) {
      setEnc(null); setErro(""); setIdem(newKey()); setVerificando(false);
      setF({ tipo: "integral", valor_pago: "", data_pagamento: hojeISO(), hora_pagamento: nowHM(),
        forma_pagamento: "PIX", juros: 0, multa: 0, desconto: 0, observacao: "", auto: true, valor_manual: false });
    }
  }, [instId]);

  useEffect(() => {
    if (!instId || !f.data_pagamento || !/^\d{2}:\d{2}$/.test(f.hora_pagamento || "")) return;
    let vivo = true; // respostas fora de ordem (data/hora editadas rápido) não sobrescrevem a prévia mais recente
    setEnc(null); // invalida a simulação anterior até o recálculo chegar (evita confirmar com valores antigos)
    api.get(`/installments/${instId}/encargos`, { params: { data: f.data_pagamento, hora: f.hora_pagamento } })
      .then((r) => { if (vivo) setEnc(r.data); }).catch(() => {});
    return () => { vivo = false; };
  }, [instId, f.data_pagamento, f.hora_pagamento]);

  useEffect(() => {
    if (!enc || !f.auto) return;
    const juros = enc.juros, multa = enc.multa, saldo = enc.saldo_principal;
    const tot = { integral: saldo + juros + multa - n(f.desconto), parcial: "", juros: juros + multa }[f.tipo];
    setF((p) => ({ ...p, juros, multa, valor_pago: tot === "" ? p.valor_pago : Math.max(0, Number(tot.toFixed(2))) }));
  }, [enc, f.tipo, f.auto, f.desconto]);

  // Ajuste manual de juros/multa: o valor recebido acompanha o novo total enquanto o operador não o digitou conscientemente
  const ajustarEncargo = (k, v) => setF((p) => {
    const nx = { ...p, [k]: v, auto: false };
    if (enc && !p.valor_manual && p.tipo !== "parcial") {
      const tot = p.tipo === "juros" ? n(nx.juros) + n(nx.multa) : enc.saldo_principal + n(nx.juros) + n(nx.multa) - n(nx.desconto);
      nx.valor_pago = Math.max(0, Number(tot.toFixed(2)));
    }
    return nx;
  });

  const principal = f.tipo === "juros" ? 0 : Math.max(0, n(f.valor_pago) - n(f.juros) - n(f.multa));
  const restante = enc ? Math.max(0, enc.saldo_principal - principal - (f.tipo === "juros" ? 0 : n(f.desconto))) : null;
  const totalDevido = enc ? (f.tipo === "juros" ? n(f.juros) + n(f.multa) : enc.saldo_principal + n(f.juros) + n(f.multa) - n(f.desconto)) : null;
  const excedente = enc ? Math.max(0, n(f.valor_pago) - Math.max(0, totalDevido)) : 0;
  const excede = excedente > 0.005;

  const submit = async () => {
    if (loading) return;
    setLoading(true); setErro("");
    try {
      const r = await api.post(`/installments/${inst.id}/pay`, {
        tipo: f.tipo, valor_pago: n(f.valor_pago), data_pagamento: f.data_pagamento, hora_pagamento: f.hora_pagamento,
        forma_pagamento: f.forma_pagamento, juros: n(f.juros), multa: n(f.multa), desconto: f.tipo === "juros" ? 0 : n(f.desconto), observacao: f.observacao,
        idempotency_key: idem, motivo_ajuste: f.motivo_ajuste || "",
      });
      toast.success(f.tipo === "juros" ? "Pagamento de juros/encargos registrado. Parcela segue em aberto." : "Baixa confirmada pelo sistema!");
      setPago({ installmentId: inst.id, paymentId: r.data?.payment_id });
      onClose(); onDone();
    } catch (e) {
      if (!e.response) {
        const ok = await verificar();
        if (ok) return;
        setVerificando(true);
        const msg = "Sem resposta do servidor. A baixa está EM VERIFICAÇÃO: não repita a operação. Use “Verificar novamente” — a mesma chave será consultada.";
        setErro(msg); toast.warning(msg);
        return;
      }
      const msg = e.response?.data?.detail || "Erro ao dar baixa";
      setErro(msg); toast.error(msg);
    } finally { setLoading(false); }
  };

  // Consulta o resultado da baixa pela MESMA idempotency_key (nunca gera nova chave nem repete a baixa sem consultar)
  const verificar = async () => {
    try {
      const st = await api.get(`/installments/${inst.id}/baixa-status`, { params: { key: idem } });
      if (st.data?.processado) {
        toast.success("Baixa confirmada pelo sistema (recuperada após falha de conexão).");
        setVerificando(false); setErro("");
        setPago({ installmentId: inst.id, paymentId: st.data.payment_id }); onClose(); onDone(); return true;
      }
      if (st.data?.em_processamento) { setErro("A baixa ainda está em processamento no servidor. Aguarde alguns segundos e verifique novamente."); return false; }
      setVerificando(false);
      setErro("Verificado: a baixa NÃO foi registrada no servidor. Você pode confirmar novamente.");
      return false;
    } catch { return false; }
  };

  return (<>
    {pago && <ComunicarClienteDialog contexto="parcela_paga" parcelaId={pago.installmentId} paymentId={pago.paymentId} pos onClose={() => setPago(null)} />}
    <Dialog open={!!inst} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="dar-baixa-modal">
        <DialogHeader>
          <DialogTitle>Registrar Pagamento — {inst?.cliente_nome}</DialogTitle>
          <DialogDescription>{rotuloParcela(inst)} · valor {brl(inst?.valor)} · vence {fmtDate(inst?.vencimento)}{enc ? ` às ${enc.horario}` : ""}</DialogDescription>
        </DialogHeader>
        {enc && <Resumo enc={enc} f={f} restante={restante} />}
        {erro && <div className={`rounded-lg border text-sm p-3 ${verificando ? "border-amber-300 bg-amber-50 text-amber-900" : "border-red-300 bg-red-50 text-red-800"}`} data-testid={verificando ? "baixa-verificacao" : "baixa-erro"}>{erro}
          {verificando ? <Button size="sm" variant="outline" onClick={verificar} className="mt-2 w-full border-amber-400" data-testid="baixa-verificar-btn">Verificar novamente</Button>
            : <span className="block text-xs mt-1">A parcela não foi marcada como paga. Corrija os dados e confirme novamente.</span>}</div>}
        {enc && (
          <div className={`rounded-lg border p-3 text-sm space-y-1 ${enc.atrasada ? "border-amber-300 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-900"}`} data-testid="encargos-box">
            <div className="font-semibold" data-testid="encargos-titulo">{enc.atrasada ? `Pagamento em atraso (${enc.dias_atraso} dia(s), ${enc.atraso_min} min após o limite)` : "Pagamento dentro do prazo"} <span className="font-normal text-xs opacity-80" data-testid="encargos-quando">· calculado para {fmtDate(f.data_pagamento)} {f.hora_pagamento} (limite {enc.horario}{enc.tolerancia_min ? ` + ${enc.tolerancia_min} min` : ""})</span></div>
            <div className="text-xs">Limite {enc.horario} + tolerância {enc.tolerancia_min} min · Saldo principal <b className="mono">{brl(enc.saldo_principal)}</b>
              {enc.atrasada && <> · Multa <b className="mono" data-testid="encargos-multa">{brl(enc.multa)}</b> · Juros <b className="mono" data-testid="encargos-juros">{brl(enc.juros)}</b></>}
              {enc.encargos_ja_pagos > 0 && <> · Encargos já pagos {brl(enc.encargos_ja_pagos)}</>} · Total devido <b className="mono" data-testid="encargos-total">{brl(enc.total_devido)}</b></div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <FF l="Tipo de pagamento" t="ff-tipo">
            <Select value={f.tipo} onValueChange={(v) => set("tipo", v)}>
              <SelectTrigger data-testid="baixa-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>{TIPOS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`baixa-tipo-${v}`}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </FF>
          <FF l="Forma de pagamento">
            <Select value={f.forma_pagamento} onValueChange={(v) => set("forma_pagamento", v)}>
              <SelectTrigger data-testid="baixa-forma"><SelectValue /></SelectTrigger>
              <SelectContent>{FORMAS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </FF>
          <FF l="Data do pagamento"><Input type="date" value={f.data_pagamento} onChange={(e) => set("data_pagamento", e.target.value)} data-testid="baixa-data" /></FF>
          <FF l="Horário do pagamento"><Input type="time" value={f.hora_pagamento} onChange={(e) => set("hora_pagamento", e.target.value)} data-testid="baixa-hora" /></FF>
          <FF l="Valor recebido (R$)"><Input type="number" step="0.01" value={f.valor_pago} onChange={(e) => setF((p) => ({ ...p, valor_pago: e.target.value, auto: false, valor_manual: true }))} data-testid="baixa-valor" /></FF>
          <FF l="Desconto (R$)"><Input type="number" step="0.01" value={f.desconto} disabled={f.tipo === "juros"} onChange={(e) => set("desconto", e.target.value)} data-testid="baixa-desconto" /></FF>
          <FF l="Juros (R$)"><Input type="number" step="0.01" value={f.juros} onChange={(e) => ajustarEncargo("juros", e.target.value)} data-testid="baixa-juros" /></FF>
          <FF l="Multa (R$)"><Input type="number" step="0.01" value={f.multa} onChange={(e) => ajustarEncargo("multa", e.target.value)} data-testid="baixa-multa" /></FF>
        </div>
        {excede && (
          <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs space-y-1 text-red-800" data-testid="baixa-excedente">
            <div>O valor recebido <b className="mono">{brl(n(f.valor_pago))}</b> excede o total devido <b className="mono">{brl(Math.max(0, totalDevido))}</b> em <b className="mono">{brl(excedente)}</b>. O principal não pode superar o saldo da parcela.</div>
            <button type="button" className="underline font-semibold" onClick={() => setF((p) => ({ ...p, valor_pago: Number(Math.max(0, totalDevido).toFixed(2)), valor_manual: false }))} data-testid="baixa-ajustar-total">Ajustar o valor recebido para o total devido</button>
          </div>
        )}
        {!f.auto && enc && <button type="button" className="text-xs text-blue-700 underline text-left" onClick={() => set("auto", true)} data-testid="baixa-recalcular">Recalcular automaticamente pelas regras do contrato</button>}
        {enc && f.tipo !== "juros" && Math.abs((n(f.juros) + n(f.multa)) - (n(enc.juros) + n(enc.multa))) > 0.005 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs space-y-1" data-testid="baixa-ajuste">
            <div className="flex flex-wrap gap-x-4"><span>Encargos calculados: <b className="mono">{brl(n(enc.juros) + n(enc.multa))}</b></span><span>Ajuste manual: <b className="mono">{brl((n(f.juros) + n(f.multa)) - (n(enc.juros) + n(enc.multa)))}</b></span><span>Total final: <b className="mono">{brl(principal + n(f.juros) + n(f.multa))}</b></span></div>
            <Input value={f.motivo_ajuste || ""} onChange={(e) => set("motivo_ajuste", e.target.value)} placeholder="Motivo do ajuste dos encargos (obrigatório)" className="h-8 text-xs bg-white" data-testid="baixa-motivo-ajuste" />
          </div>
        )}
        {enc && (
          <div className="rounded-lg bg-slate-50 border px-3 py-2 text-xs flex flex-wrap gap-x-4 gap-y-1" data-testid="baixa-resumo">
            <span>Principal: <b className="mono" data-testid="resumo-principal">{brl(principal)}</b></span>
            <span>Encargos: <b className="mono">{brl(Math.min(n(f.valor_pago), n(f.juros) + n(f.multa)))}</b></span>
            <span>Saldo principal após: <b className="mono" data-testid="resumo-restante">{brlOrDash(restante)}</b></span>
            {f.tipo === "juros" && <span className="text-amber-700 font-semibold w-full" data-testid="aviso-so-juros">A parcela principal continuará pendente com o saldo original.</span>}
          </div>
        )}
        <FF l="Observação"><Textarea value={f.observacao} onChange={(e) => set("observacao", e.target.value)} rows={2} data-testid="baixa-obs" /></FF>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="baixa-cancelar">Cancelar</Button>
          <Button onClick={submit} disabled={loading || !enc || verificando || excede} className="bg-gold hover:bg-[hsl(var(--gold-hover))] text-black font-semibold" data-testid="baixa-confirm-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar Baixa"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>);
}
